import { Card, Space } from 'antd';
import { Fragment, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { config } from '@app/config';
import {
  ACTIONS_HISTORY_PATH,
  BRANCHES_PATH,
  CATEGORIES_PATH,
  CHAINS_PATH,
  CLIENT_SERVICES_PATH,
  DATA_MIGRATION_PATH,
  EMPLOYEES_PATH,
  INTEGRATIONS_PATH,
  LEGAL_ENTITIES_PATH,
  MESSAGES_PATH,
  OPERATIONS_PATH,
  PAYMENTS_ACCOUNTS_PATH,
  POSITIONS_PATH,
  PREFIX,
  PROFILE_PATH,
  SALE_PLANS_PATH,
  SCHEDULE_PATH,
  TAGS_PATH,
  CLIENT_FIELDS_PATH,
  SUBSCRIPTIONS_PATH,
  CERTIFICATES_PATH,
  USERS_PATH,
  AI_ACTIONS_HISTORY_PATH,
  AI_PROMPTS_SETTINGS_PATH,
  PAID_AI_FEATURES_PATH,
  MESSAGE_TEMPLATES_PATH,
  CALENDAR_SETTINGS_PATH,
  NOTIFICATIONS_PATH,
  SUPPLIERS_PATH,
  BRANDS_PATH,
  PRODUCT_CATEGORIES_PATH,
  GOODS_UNITS_PATH,
  SETTING_UP_AUTOMATIC_MESSAGING_TO_CLIENTS_PATH,
  PURCHASE_PLANNING_SETTINGS_PATH,
  NEURAL_NETWORK_LOGS_SETTINGS_PATH,
  DETACH_RESPONSIBLE_FROM_LEAD_PATH,
  DOCUMENT_TEMPLATES_PATH,
  MCP_INTEGRATION_PATH,
  PROXY_PATH,
} from '@app/constants';
import { useAccessRightsToAi, useNewUserAccessRights } from '@app/hooks';
import { NNavigation } from '@app/types';
import { generateKey } from '@app/utils';

import { useMcpAccess } from '@pages/McpIntegration';
import { useProxyAccess } from '@pages/ProxyIntegration';
import { menu } from '@pages/Settings/data';

import '@pages/Settings/styles/Settings.mobile.scss';

interface PageRights {
  [path: string]: boolean;
}

export const Settings = () => {
  const { userAccessRights, organizationAccessRights } =
    useNewUserAccessRights();
  const { allowed: hasMcpAccess } = useMcpAccess();
  const { visible: isProxyVisible } = useProxyAccess();

  const {
    Warehouse: { IsSuppliers },
    Products: { IsBrands, IsProductCategories },
    Users: {
      IsOn: hasUserRightToViewEmployees,
      IsUsers: hasUserRightToViewUsers,
      IsEmployees: isOnlyEmployeesRegister,
    },
    LegalEntities: { IsOn: hasUserRightToViewLegalEntities },
    SalesRegister: { IsOn: hasUserRightToViewOperations },
    Chains: { IsOn: hasUserRightToViewChains },
    Branches: { IsOn: hasUserRightToViewBranches },
    ClientServices: {
      IsOn: hasUserRightToViewServices,
      IsClientServices: rightForServicesRegister,
      IsServiceCategories: rightForCategoriesRegister,
    },
    Positions: { IsOn: hasUserRightToViewPositions },
    PaymentAccounts: { IsOn: hasUserRightToViewPaymentsAccounts },
    SalePlans: { IsOn: hasUserRightToViewSalePlans },
    DataMigrations: { IsOn: hasUserRightToViewMigration },
    Integrations: { IsOn: hasUserRightToViewIntegrations },
    History: { IsOn: hasUserRightToGeneralHistory },
    WorkSchedules: { IsScheduleView: hasUserRightToViewSchedules },
    Leads: { IsOn: hasUserRightToViewLeads },
    MessageTemplates: { IsOn: hasUserRightToMessageTemplatesModule },
    Records: { IsViewReminders },
  } = userAccessRights;
  const { hasOrganizationRightToAi, hasUserSpecialRightToAi } =
    useAccessRightsToAi();

  const deleteUnusedPages = (
    items: NNavigation.PagesGroupInSettings['items'],
    pageRights: PageRights
  ): NNavigation.PagesGroupInSettings['items'] => {
    return items.filter(({ path }: NNavigation.PageInSettings) => {
      return pageRights[path];
    });
  };

  const warehouseModuleAllowed =
    organizationAccessRights.find(({ RightType }) => RightType === 9)
      ?.Allowed || false;

  const pageRights = {
    [TAGS_PATH]: userAccessRights.Tags.IsOn,
    [CLIENT_FIELDS_PATH]: userAccessRights.Clients.IsOn,
    [SUBSCRIPTIONS_PATH]: userAccessRights.Clients.IsOn,
    [CERTIFICATES_PATH]: userAccessRights.Clients.IsOn,
    [POSITIONS_PATH]:
      hasUserRightToViewEmployees && hasUserRightToViewPositions,
    [LEGAL_ENTITIES_PATH]: hasUserRightToViewLegalEntities,
    [OPERATIONS_PATH]: hasUserRightToViewOperations,
    [CHAINS_PATH]: hasUserRightToViewChains,
    [BRANCHES_PATH]: hasUserRightToViewBranches,
    [CLIENT_SERVICES_PATH]:
      hasUserRightToViewServices && rightForServicesRegister,
    [CATEGORIES_PATH]: hasUserRightToViewServices && rightForCategoriesRegister,
    [PAYMENTS_ACCOUNTS_PATH]: hasUserRightToViewPaymentsAccounts,
    [SALE_PLANS_PATH]: hasUserRightToViewSalePlans,
    [INTEGRATIONS_PATH]: hasUserRightToViewIntegrations,
    [DATA_MIGRATION_PATH]: hasUserRightToViewMigration,
    [ACTIONS_HISTORY_PATH]: hasUserRightToGeneralHistory,
    [USERS_PATH]: hasUserRightToViewUsers,
    [EMPLOYEES_PATH]: isOnlyEmployeesRegister,
    [SCHEDULE_PATH]: hasUserRightToViewSchedules,
    [MESSAGES_PATH]: hasUserRightToViewLeads,
    [MESSAGE_TEMPLATES_PATH]: hasUserRightToMessageTemplatesModule,
    [PROFILE_PATH]: true,
    [AI_ACTIONS_HISTORY_PATH]: true,
    [CALENDAR_SETTINGS_PATH]: true,
    [NOTIFICATIONS_PATH]: IsViewReminders,
    [SUPPLIERS_PATH]:
      IsSuppliers && warehouseModuleAllowed && userAccessRights.Warehouse.IsOn,
    [BRANDS_PATH]:
      IsBrands && warehouseModuleAllowed && userAccessRights.Warehouse.IsOn,
    [PRODUCT_CATEGORIES_PATH]:
      IsProductCategories &&
      warehouseModuleAllowed &&
      userAccessRights.Products.IsOn,
    [GOODS_UNITS_PATH]:
      warehouseModuleAllowed && userAccessRights.Products.IsOn,
    [SETTING_UP_AUTOMATIC_MESSAGING_TO_CLIENTS_PATH]: true,
    [AI_PROMPTS_SETTINGS_PATH]: true,
    [PAID_AI_FEATURES_PATH]: true,
    [PURCHASE_PLANNING_SETTINGS_PATH]:
      warehouseModuleAllowed &&
      userAccessRights.Warehouse.IsOn &&
      userAccessRights.Products.IsOn,
    [NEURAL_NETWORK_LOGS_SETTINGS_PATH]: true,
    [DETACH_RESPONSIBLE_FROM_LEAD_PATH]: true,
    [DOCUMENT_TEMPLATES_PATH]: true,
    // На проде MCP открыт только пилотным пользователям (src/access).
    [MCP_INTEGRATION_PATH]: hasMcpAccess && Boolean(config.mcpResourceOrigin),
    [PROXY_PATH]: isProxyVisible,
  };

  const checkForAccessToGroup = useCallback(
    (groupName: NNavigation.EGroupNameInSettings): boolean => {
      if (groupName !== NNavigation.EGroupNameInSettings.AI_OPERATOR) {
        return true;
      }

      if (!hasOrganizationRightToAi) {
        return false;
      }

      return hasUserSpecialRightToAi;
    },
    [hasOrganizationRightToAi, hasUserSpecialRightToAi]
  );

  return (
    <div className={`${PREFIX}settings`}>
      <Space size="large" align="start" wrap>
        {menu.map(({ groupName, groupDataTestId, items }, index) => {
          if (deleteUnusedPages(items, pageRights).length > 0) {
            return (
              <Fragment key={groupDataTestId}>
                {checkForAccessToGroup(groupName) ? (
                  <Card
                    key={generateKey('Settings', 'group', groupName, index)}
                    type="inner"
                    title={groupName}
                    style={{ width: 264 }}
                    data-testid={groupDataTestId}
                  >
                    <Space direction="vertical">
                      {deleteUnusedPages(items, pageRights).map(
                        (pageMeta, index) => {
                          const { name, path, dataTestId } = pageMeta;
                          const isAiOperatorHistory =
                            path === AI_ACTIONS_HISTORY_PATH;

                          if (
                            isAiOperatorHistory &&
                            !hasUserRightToGeneralHistory
                          ) {
                            return <></>;
                          }

                          return (
                            <Link
                              key={generateKey(
                                'Settings',
                                'group-item',
                                name,
                                index
                              )}
                              to={path}
                              data-testid={dataTestId}
                            >
                              {name}
                            </Link>
                          );
                        }
                      )}
                    </Space>
                  </Card>
                ) : (
                  <></>
                )}
              </Fragment>
            );
          }
        })}
      </Space>
    </div>
  );
};
